import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { adminNav } from '@/components/px/nav'
import { AccessGroupsPanel } from '@/features/org/components/AccessGroupsPanel'
import { OrgSettingsForm } from '@/features/org/components/OrgSettingsForm'
import { SecuritySettingsForm } from '@/features/org/components/SecuritySettingsForm'
import { TenantPolicyPanel } from '@/features/org/components/TenantPolicyPanel'
import { cn } from '@/lib/utils'

type Tab = 'organisation' | 'security' | 'accessGroups' | 'tenantPolicy'

const TABS: readonly { key: Tab; labelKey: string }[] = [
  { key: 'organisation', labelKey: 'tabs.organisation' },
  { key: 'security', labelKey: 'tabs.security' },
  { key: 'accessGroups', labelKey: 'tabs.accessGroups' },
  { key: 'tenantPolicy', labelKey: 'tabs.tenantPolicy' }
]

/**
 * Settings (`/admin/settings`, PX-W6.2): one screen, four sections behind
 * local tabs — organisation profile, security policy, access groups and the
 * read-only tenant policy. Every section carries its own triad + 403 state.
 */
export default function OrgSettingsPage() {
  const { t } = useTranslation(['orgSettings', 'people'])
  const [tab, setTab] = useState<Tab>('organisation')

  return (
    <AppShell
      moduleLabel={t('people:title')}
      breadcrumb={<span>{t('orgSettings:title')}</span>}
      contextPill={t('people:chrome.contextPill')}
      nav={adminNav('admin')}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{t('orgSettings:title')}</h1>
        <p className="text-sm text-muted-foreground">{t('orgSettings:lede')}</p>
      </header>

      <div
        role="tablist"
        aria-label={t('orgSettings:title')}
        className="mt-4 flex flex-wrap gap-1.5"
      >
        {TABS.map(({ key, labelKey }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={cn(
              'rounded-full border px-3 py-1 text-sm transition-colors',
              tab === key
                ? 'border-primary bg-primary font-medium text-primary-foreground'
                : 'border-border bg-card text-ink-soft hover:bg-muted'
            )}
          >
            {t(labelKey)}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {tab === 'organisation' && <OrgSettingsForm />}
        {tab === 'security' && <SecuritySettingsForm />}
        {tab === 'accessGroups' && <AccessGroupsPanel />}
        {tab === 'tenantPolicy' && <TenantPolicyPanel />}
      </div>
    </AppShell>
  )
}
